import * as React from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Save, ShieldCheck, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Switch } from '../../../../components/ui/switch';
import { Button } from '../../../../components/ui/button';
import { useToast } from '../../../../components/ui/toast';
import { useSecretStatus, useUpdateApiKeys, type ApiKeysInput } from '../../../../hooks/useSecretStatus';
import { LiveKitCard } from './LiveKitCard';
import { useUser } from '../../../../auth/AuthProvider';
import { usePhoneSettingsForm } from '../PhoneSettingsContext';

interface KeyRow {
  field: keyof ApiKeysInput;
  secretName: string;
  label: string;
  hint: string;
  signupUrl: string;
  /** Predicate that returns null if the value looks like a key from this
   *  provider, or a short reason string when the prefix/length doesn't
   *  match. Used to catch the "wrong-key-in-wrong-field" paste error
   *  (e.g. ElevenLabs `sk_…` pasted into Google AI which expects `AIza…`). */
  validate?: (raw: string) => string | null;
  /** Human-readable description of the expected shape — shown in the error. */
  expected?: string;
}

const KEYS: ReadonlyArray<KeyRow> = [
  {
    field: 'openaiKey',
    secretName: 'OPENAI_API_KEY',
    label: 'OpenAI',
    hint: 'GPT-4o, GPT-4.1 (Default-LLM für niedrige Latenz)',
    signupUrl: 'https://platform.openai.com/api-keys',
    expected: 'sk-… oder sk-proj-…',
    validate: (v) =>
      v.startsWith('sk-') ? null : 'OpenAI-Keys beginnen mit `sk-` oder `sk-proj-`.',
  },
  {
    field: 'anthropicKey',
    secretName: 'ANTHROPIC_API_KEY',
    label: 'Anthropic',
    hint: 'Claude Haiku/Sonnet/Opus',
    signupUrl: 'https://console.anthropic.com/settings/keys',
    expected: 'sk-ant-…',
    validate: (v) =>
      v.startsWith('sk-ant-') ? null : 'Anthropic-Keys beginnen mit `sk-ant-`.',
  },
  {
    field: 'googleAiKey',
    secretName: 'GOOGLE_AI_API_KEY',
    label: 'Google Gemini',
    hint: 'Gemini 2.5 Flash/Pro',
    signupUrl: 'https://aistudio.google.com/app/apikey',
    expected: 'AIza… (39 Zeichen)',
    validate: (v) =>
      v.startsWith('AIza') && v.length === 39
        ? null
        : 'Google-AI-Keys beginnen mit `AIza` und sind genau 39 Zeichen lang. Hol ihn unter aistudio.google.com/app/apikey.',
  },
  {
    field: 'groqKey',
    secretName: 'GROQ_API_KEY',
    label: 'Groq',
    hint: 'Llama / Mixtral mit ultra-niedrigem TTFT',
    signupUrl: 'https://console.groq.com/keys',
    expected: 'gsk_…',
    validate: (v) =>
      v.startsWith('gsk_') ? null : 'Groq-Keys beginnen mit `gsk_`.',
  },
  {
    field: 'mistralKey',
    secretName: 'MISTRAL_API_KEY',
    label: 'Mistral',
    hint: 'Mistral Large / NeMo / Ministral',
    signupUrl: 'https://console.mistral.ai/api-keys',
    expected: '32 alphanumerische Zeichen',
    validate: (v) =>
      /^[A-Za-z0-9]{32}$/.test(v)
        ? null
        : 'Mistral-Keys sind 32-stellige alphanumerische Strings (kein Prefix).',
  },
  {
    field: 'openrouterKey',
    secretName: 'OPENROUTER_API_KEY',
    label: 'OpenRouter',
    hint: 'Beliebige Modelle (Qwen, Llama, weitere Claude-Modelle …)',
    signupUrl: 'https://openrouter.ai/keys',
    expected: 'sk-or-…',
    validate: (v) =>
      v.startsWith('sk-or-') ? null : 'OpenRouter-Keys beginnen mit `sk-or-`.',
  },
  {
    field: 'xaiKey',
    secretName: 'XAI_API_KEY',
    label: 'xAI (Grok)',
    hint: 'Grok 4.x — inkl. Voice-Agent-API als Alternative zu ElevenLabs.',
    signupUrl: 'https://console.x.ai/team/default/api-keys',
    expected: 'xai-…',
    validate: (v) =>
      v.startsWith('xai-') ? null : 'xAI-Keys beginnen mit `xai-`.',
  },
  {
    field: 'elevenlabsKey',
    secretName: 'ELEVENLABS_API_KEY',
    label: 'ElevenLabs',
    hint: 'TTS — Pflicht für Voice-Agent',
    signupUrl: 'https://elevenlabs.io/app/settings/api-keys',
    expected: 'sk_… (Underscore)',
    validate: (v) =>
      v.startsWith('sk_')
        ? null
        : 'ElevenLabs-Keys beginnen mit `sk_` (Underscore — nicht zu verwechseln mit OpenAIs `sk-`).',
  },
  {
    field: 'deepgramKey',
    secretName: 'DEEPGRAM_API_KEY',
    label: 'Deepgram',
    hint: 'STT — Pflicht für Voice-Agent (Default)',
    signupUrl: 'https://console.deepgram.com/project',
    expected: '40 hex Zeichen',
    validate: (v) =>
      /^[a-f0-9]{40}$/.test(v)
        ? null
        : 'Deepgram-Keys sind 40 Zeichen Hex (0-9 a-f).',
  },
];

export function ApiKeysTab() {
  const status = useSecretStatus();
  const { toast } = useToast();
  const update = useUpdateApiKeys();
  const [draft, setDraft] = React.useState<ApiKeysInput>({});
  const { isAdmin } = useUser();
  const { form, setForm } = usePhoneSettingsForm();
  const useCentral = form.useCentralApiKeys === true;

  const dirty = Object.values(draft).some((v) => v && v.length > 0);

  // Per-field validation result. Empty value → no error (means "leave
  // unchanged"). Non-empty value → run the provider-specific validator.
  const errors = React.useMemo(() => {
    const out: Partial<Record<keyof ApiKeysInput, string>> = {};
    for (const k of KEYS) {
      const v = (draft[k.field] ?? '').trim();
      if (!v) continue;
      if (k.validate) {
        const err = k.validate(v);
        if (err) out[k.field] = err;
      }
    }
    return out;
  }, [draft]);
  const hasErrors = Object.keys(errors).length > 0;

  async function onSave() {
    try {
      const r = await update.mutateAsync(draft);
      setDraft({});
      toast({
        title: 'API-Schlüssel gespeichert',
        description:
          r.written.length === 0
            ? 'Keine Änderungen.'
            : `${r.written.length} Schlüssel aktualisiert.`,
      });
    } catch (err) {
      toast({
        title: 'Speichern fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <div className="space-y-6">
      {/* Admin-only routing toggle. Lets the second-tier admin choose
          between using the platform's central API-keys (managed by the
          bootstrap admin under Verwaltung → Platform-Keys) or their
          own personal per-user keys hinterlegt darunter. The voice-
          agent reads `useCentralApiKeys` from PhoneSettings and skips
          the per-user secret lookup when this is `true`. Pro/Enterprise
          users never see this Tab; Ultimate users always BYO-keys, so
          we only show the toggle for admins. */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              API-Key-Quelle
            </CardTitle>
            <CardDescription>
              Wähle, ob der Voice-Agent für deinen Account die zentralen
              Plattform-Schlüssel oder deine persönlichen Schlüssel nutzen soll.
              Persönliche Schlüssel bleiben gespeichert, auch wenn du gerade die
              zentralen verwendest — du kannst jederzeit zurückwechseln.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between gap-3 rounded-md border border-border p-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">
                  {useCentral ? 'Zentrale Plattform-Schlüssel' : 'Persönliche Schlüssel'}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {useCentral
                    ? 'Der Agent nutzt die unter Verwaltung → Entwicklereinstellungen → Platform-Keys hinterlegten Schlüssel. Eingaben unten bleiben für später erhalten.'
                    : 'Der Agent nutzt die unten hinterlegten Schlüssel. Wenn ein Slot leer ist, fällt er auf den Plattform-Default zurück.'}
                </p>
              </div>
              <Switch
                checked={useCentral}
                onCheckedChange={(v) =>
                  setForm((p) => ({ ...p, useCentralApiKeys: v }))
                }
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Die Änderung wird mit deinem nächsten Klick auf „Speichern" oben
              persistiert. Pro/Enterprise-User sehen diesen Tab nicht — sie
              nutzen immer die zentralen Schlüssel. Ultimate-User nutzen immer
              ihre eigenen.
            </p>
          </CardContent>
        </Card>
      )}

      <Card className={useCentral ? 'opacity-60' : undefined}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Provider-API-Schlüssel
          </CardTitle>
          <CardDescription>
            {useCentral
              ? 'Aktuell deaktiviert — der Agent nutzt die zentralen Schlüssel. Du kannst hier trotzdem Werte hinterlegen, sie werden aktiv sobald du oben auf „Persönliche Schlüssel" zurückwechselst.'
              : 'Schlüssel werden verschlüsselt in Google Secret Manager gespeichert — niemals in Firestore. Eingegebene Werte sind nur einmal sichtbar; danach zeigen wir nur einen „gespeichert"-Status.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {KEYS.map((k) => {
            const present = status.data?.apiKeys[k.secretName] ?? false;
            const value = draft[k.field] ?? '';
            const error = errors[k.field];
            return (
              <div key={k.field} className="space-y-1">
                <div className="grid items-center gap-3 sm:grid-cols-[180px_1fr_auto]">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={k.field}>{k.label}</Label>
                      {status.isLoading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      ) : present ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
                          <CheckCircle2 className="h-3 w-3" /> gespeichert
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-400">
                          <XCircle className="h-3 w-3" /> fehlt
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {k.hint}
                      {k.expected && (
                        <span className="ml-1 text-muted-foreground/70">
                          · Format: <code className="font-mono">{k.expected}</code>
                        </span>
                      )}
                    </p>
                  </div>
                  <Input
                    id={k.field}
                    type="password"
                    placeholder={present ? '••••••••  (leer = unverändert)' : 'API-Schlüssel einfügen'}
                    value={value}
                    onChange={(e) => setDraft((d) => ({ ...d, [k.field]: e.target.value }))}
                    autoComplete="off"
                    aria-invalid={!!error}
                    className={error ? 'border-destructive focus-visible:ring-destructive' : undefined}
                  />
                  <a
                    href={k.signupUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Schlüssel holen ↗
                  </a>
                </div>
                {error && (
                  <div className="flex items-start gap-1.5 pl-[180px] text-xs text-destructive">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
              </div>
            );
          })}

          <div className="flex items-center justify-end gap-3 pt-2">
            {hasErrors && (
              <span className="text-xs text-destructive">
                Bitte korrigiere die markierten Felder vor dem Speichern.
              </span>
            )}
            <Button onClick={onSave} disabled={!dirty || hasErrors || update.isPending}>
              {update.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Schlüssel speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      <LiveKitCard />

      <Card>
        <CardHeader>
          <CardTitle>Welche Schlüssel brauche ich wirklich?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Pflicht für Voice-Agent:</strong> ElevenLabs (TTS) +
            Deepgram (STT) + mindestens ein LLM-Provider, der zum gewählten „KI-Modell"-Tab passt.
          </p>
          <p>
            <strong className="text-foreground">Optional:</strong> alle weiteren LLM-Provider, falls
            du im Tab „KI-Modell" zwischen ihnen wechseln willst.
          </p>
          <p>
            <strong className="text-foreground">Pro-Anrufer-Schlüssel</strong> wie der Twilio
            Auth-Token werden im Tab „Anbieter & Nummer" eingegeben — die landen pro User
            namespaced in Secret Manager (`TWILIO_AUTH_TOKEN__&lt;uid&gt;`).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
